import React, { useState, useMemo, useCallback, useEffect } from "react";
import { createFileRoute } from "@tanstack/react-router";
import {
  CreditCard,
  CheckCircle,
  XCircle,
  Clock,
  Loader2,
  RefreshCw,
  Plus,
  MoreHorizontal,
  ArrowUp,
  Ban,
  RotateCcw,
  Building2,
  ChevronRight,
  ChevronDown,
  CalendarClock,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { SubscriptionBillingStartDialog } from "@/components/portal/platform/SubscriptionBillingStartDialog";
import { SearchInput } from "@/components/ui/search-input";
import {
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Pagination } from "@/components/ui/pagination";
import { SortableTableHead } from "@/components/ui/sortable-table-head";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { categoryLabel } from "@/lib/serviceCategory";
import { usePortalData } from "@/components/portal/PortalDataProvider";
import {
  BUNDLED_ANCHOR_HINT,
  hasBundledAnchor,
  subscriptionsForEntitlement,
} from "@/components/portal/apps/reporting/custom/entitlements";
import { usePermissions } from "@/hooks/usePermissions";
import {
  platformApi,
  Subscription,
  PlatformService,
  ServiceTier,
} from "@/services/api/platform";
import type { B2CClientConnector } from "@/types/portal";
import { portalFetch } from "@/lib/portalApi";
import { tierChangeToast } from "@/lib/tierChangeToast";
import { usePaginatedData, PaginatedResponse } from "@/hooks/usePaginatedData";

export const Route = createFileRoute("/portal/platform/subscriptions")({
  component: SubscriptionsPage,
});

type ViewMode = "organisation" | "all";

function SubscriptionsPage() {
  const { token, user } = useAuth();
  const { selectedOrganisation } = usePortalData();
  const { can } = usePermissions();
  const canManageSubscriptions = can("manage", "subscriptions");
  const isPlatformAdmin = user?.is_admin === true;

  // View mode: org-scoped (default) or all orgs (admin only)
  const [viewMode, setViewMode] = useState<ViewMode>("organisation");
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [categoryFilter, setCategoryFilter] = useState<string>("all");
  const [sortBy, setSortBy] = useState("service_name");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("asc");

  // Add subscription dialog state
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [availableServices, setAvailableServices] = useState<PlatformService[]>(
    [],
  );
  const [selectedService, setSelectedService] = useState<string>("");
  const [selectedTier, setSelectedTier] = useState<string>("");
  const [serviceTiers, setServiceTiers] = useState<ServiceTier[]>([]);
  const [isLoadingTiers, setIsLoadingTiers] = useState(false);
  const [isAdding, setIsAdding] = useState(false);

  // Connector service dialog state
  const [connectorInstanceId, setConnectorInstanceId] = useState<string>("");
  const [b2cConnectors, setB2cConnectors] = useState<B2CClientConnector[]>([]);
  const [isLoadingB2cConnectors, setIsLoadingB2cConnectors] = useState(false);
  const [b2cFetchFailed, setB2cFetchFailed] = useState(false);

  // Upgrade dialog state
  const [showUpgradeDialog, setShowUpgradeDialog] = useState(false);
  const [subscriptionToUpgrade, setSubscriptionToUpgrade] =
    useState<Subscription | null>(null);
  const [upgradeTiers, setUpgradeTiers] = useState<ServiceTier[]>([]);
  const [newTier, setNewTier] = useState<string>("");
  const [isUpgrading, setIsUpgrading] = useState(false);

  // Cancel dialog state
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [subscriptionToCancel, setSubscriptionToCancel] =
    useState<Subscription | null>(null);
  const [cancelImmediately, setCancelImmediately] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);

  // Billing start dialog (platform admin only, cp #843)
  const [subscriptionForBillingStart, setSubscriptionForBillingStart] =
    useState<Subscription | null>(null);

  // Feature visibility (effective features per subscription)
  const [featuresCache, setFeaturesCache] = useState<
    Record<string, Record<string, unknown>[]>
  >({});
  const [featuresLoading, setFeaturesLoading] = useState<Set<string>>(new Set());
  const [expandedFeatures, setExpandedFeatures] = useState<Set<string>>(new Set());

  // Org-scoped data (simple fetch, no server-side pagination needed)
  const [orgData, setOrgData] = useState<Subscription[]>([]);
  // `orgData` is `[]` both when the org genuinely holds nothing and when the
  // fetch FAILED; only the flag tells them apart (same contract as
  // PortalDataProvider's `activeServicesFailed`).
  const [orgDataFailed, setOrgDataFailed] = useState(false);
  const [orgLoading, setOrgLoading] = useState(false);

  const selectedOrgId = selectedOrganisation?.organisationId;
  const currentOrgName =
    selectedOrganisation?.organisationName || "Organisation";

  const isOrgView = viewMode === "organisation";

  // Reset view state when switching modes
  function handleViewModeChange(mode: ViewMode) {
    setViewMode(mode);
    setSearchTerm("");
    setStatusFilter("all");
    setSortBy(mode === "organisation" ? "service_name" : "organisation_name");
    setSortOrder("asc");
  }

  // ---- Org-scoped data fetching ----
  const fetchOrgData = useCallback(async () => {
    if (!token || !selectedOrgId) {
      setOrgData([]);
      return;
    }
    setOrgLoading(true);
    try {
      const subs = await platformApi.getOrgServiceSubscriptions(token, selectedOrgId);
      // The response may not include organisation_name — populate from context
      const mapped = subs.map((sub) => ({
        ...sub,
        organisation_name: currentOrgName,
      }));
      setOrgData(mapped);
      setOrgDataFailed(false);
    } catch (error) {
      console.error("Failed to load subscriptions:", error);
      toast.error("Failed to load subscriptions");
      // `[]` here is a FAILURE default, not an answer — the flag is what
      // stops it reading as "this org holds nothing" downstream.
      setOrgData([]);
      setOrgDataFailed(true);
    } finally {
      setOrgLoading(false);
    }
  }, [token, selectedOrgId, currentOrgName]);

  useEffect(() => {
    if (isOrgView) {
      fetchOrgData();
    }
  }, [isOrgView, fetchOrgData]);

  // Client-side filter/sort for org view
  const filteredOrgData = useMemo(() => {
    let filtered = [...orgData];
    if (statusFilter !== "all") {
      filtered = filtered.filter((s) => s.status === statusFilter);
    }
    if (categoryFilter !== "all") {
      filtered = filtered.filter((s) => s.category === categoryFilter);
    }
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (s) =>
          s.service_name.toLowerCase().includes(term) ||
          (s.service_display_name || "").toLowerCase().includes(term),
      );
    }
    filtered.sort((a, b) => {
      const aVal = (a[sortBy as keyof Subscription] || "").toString().toLowerCase();
      const bVal = (b[sortBy as keyof Subscription] || "").toString().toLowerCase();
      return sortOrder === "asc" ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
    });
    return filtered;
  }, [orgData, statusFilter, categoryFilter, searchTerm, sortBy, sortOrder]);

  // ---- Admin (all orgs) data fetching — server-side paginated ----
  const adminFetchFn = useCallback(
    async (
      page: number,
      pageSize: number,
    ): Promise<PaginatedResponse<Subscription>> => {
      if (!token) {
        return { data: [], totalCount: 0, page, pageSize };
      }
      try {
        const response = await platformApi.listSubscriptions(token, {
          offset: (page - 1) * pageSize,
          limit: pageSize,
          status: statusFilter !== "all" ? statusFilter : undefined,
          category: categoryFilter !== "all" ? categoryFilter : undefined,
          search: searchTerm || undefined,
          sort_by: sortBy,
          sort_order: sortOrder,
        });
        return {
          data: response.items || [],
          totalCount: response.total,
          page,
          pageSize,
        };
      } catch (error) {
        console.error("Failed to load subscriptions:", error);
        toast.error("Failed to load subscriptions");
        return { data: [], totalCount: 0, page, pageSize };
      }
    },
    [token, statusFilter, categoryFilter, searchTerm, sortBy, sortOrder],
  );

  const {
    data: adminData,
    isLoading: adminLoading,
    pagination,
    goToPage,
    nextPage,
    prevPage,
    firstPage,
    lastPage,
    setPageSize,
    refresh: adminRefresh,
    canGoNext,
    canGoPrev,
    startIndex,
    endIndex,
  } = usePaginatedData<Subscription>({
    fetchFn: adminFetchFn,
    initialPageSize: 25,
    dependencies: [token, statusFilter, categoryFilter, searchTerm, sortBy, sortOrder],
  });

  // Unified data/loading/refresh based on view mode
  const data = isOrgView ? filteredOrgData : adminData;

  // ---- Workflows <-> MyApp Professional pairing (item 6) -------------------
  // Mirrors the backend rule, which is the enforcement: the API 422s a lone
  // cancel that would strand the other half. This is so the user is asked
  // rather than shown a rejection.
  const PAIRED_WORKFLOWS = "workflows";
  const PAIRED_MYAPP = "myapp";
  const PAIRED_MYAPP_TIER = "professional";

  // Reporting Bundled is included with a connector or Workflows subscription
  // and the CP refuses it otherwise (400, `BUNDLED_ANCHOR_HINT`). Same rule
  // as item 6 below — offering the option and then rejecting the save is
  // worse than not offering it — so both pickers filter `bundled` when this
  // org's own subscription list (all statuses; the predicate keeps live
  // ones) holds no anchor.
  //
  // ONLY when we actually KNOW. `orgData` is `[]` before the first fetch
  // lands and on a failed one, it still holds the PREVIOUS org's rows for
  // the window after an org switch (`fetchOrgData` never clears it), and in
  // the ADMIN "all organisations" view it is never fetched at all while the
  // table still renders every org's rows. Filtering in any of those states
  // would hide `bundled` from orgs that plainly hold anchors. Unknown ⇒
  // offer it and let the CP refuse (its detail is in the toast); the FE
  // must never be stricter than the BE.
  const anchorKnown = isOrgView && !orgDataFailed && !orgLoading;
  const orgHasBundledAnchor =
    anchorKnown &&
    hasBundledAnchor(subscriptionsForEntitlement(orgData, orgDataFailed)) === true;
  const hideReportingBundled = (serviceName: string | undefined): boolean =>
    anchorKnown &&
    (serviceName ?? "").toLowerCase() === "reporting" &&
    !orgHasBundledAnchor;
  const withoutBundled = (tiers: ServiceTier[], serviceName: string | undefined) =>
    hideReportingBundled(serviceName)
      ? tiers.filter((t) => t.name.toLowerCase() !== "bundled")
      : tiers;

  function isPairedHalf(serviceName: string, tier: string | null): boolean {
    if (serviceName === PAIRED_WORKFLOWS) return true;
    return (
      serviceName === PAIRED_MYAPP &&
      (tier ?? "").toLowerCase() === PAIRED_MYAPP_TIER
    );
  }

  function otherHalfOf(serviceName: string): string {
    return serviceName === PAIRED_WORKFLOWS ? PAIRED_MYAPP : PAIRED_WORKFLOWS;
  }

  /** Is the OTHER half of the pair active for the same organisation? */
  function holdsOtherHalf(subscription: Subscription | null): boolean {
    if (!subscription) return false;
    if (!isPairedHalf(subscription.service_name, subscription.tier)) return false;
    const other = otherHalfOf(subscription.service_name);
    return data.some(
      (candidate) =>
        candidate.organisation_id === subscription.organisation_id &&
        candidate.service_name === other &&
        ["active", "trialing", "trial"].includes(
          (candidate.status ?? "").toLowerCase(),
        ) &&
        (other !== PAIRED_MYAPP ||
          (candidate.tier ?? "").toLowerCase() === PAIRED_MYAPP_TIER),
    );
  }

  const cancelStrandsPair = holdsOtherHalf(subscriptionToCancel);
  const isLoading = isOrgView ? orgLoading : adminLoading;
  const refresh = isOrgView ? fetchOrgData : adminRefresh;

  // Load available services on mount (needed by both add and upgrade dialogs)
  useEffect(() => {
    if (token) {
      platformApi
        .listPlatformServices(token, false)
        .then(setAvailableServices)
        .catch((err) => {
          console.error("Failed to load services:", err);
        });
    }
  }, [token]);

  // Load tiers when a service is selected in add dialog
  useEffect(() => {
    if (!selectedService || !token) {
      setServiceTiers([]);
      setSelectedTier("");
      setB2cConnectors([]);
      setConnectorInstanceId("");
      setB2cFetchFailed(false);
      return;
    }
    const service = findServiceByName(availableServices, selectedService);
    if (!service) return;

    // Reset connector state
    setB2cConnectors([]);
    setConnectorInstanceId("");
    setB2cFetchFailed(false);

    // Load tiers
    setIsLoadingTiers(true);
    setSelectedTier("");
    platformApi
      .listTiersForService(token, service.id)
      .then((tiers) => setServiceTiers(tiers.filter((t) => t.is_active)))
      .catch((err) => {
        console.error("Failed to load tiers:", err);
        toast.error("Failed to load tiers for this service");
      })
      .finally(() => setIsLoadingTiers(false));

    // For connector services, also fetch B2C connector instances for the org
    if (service.category === "connector" && selectedOrgId) {
      setIsLoadingB2cConnectors(true);
      portalFetch<B2CClientConnector[]>(
        `/api/v1/gateway/business2cloud/api/v1/connectors/org/${selectedOrgId}`,
        { token },
      )
        .then((connectors) => {
          setB2cConnectors(connectors ?? []);
          setB2cFetchFailed(false);
        })
        .catch(() => {
          setB2cConnectors([]);
          setB2cFetchFailed(true);
        })
        .finally(() => setIsLoadingB2cConnectors(false));
    }
  }, [selectedService, token, availableServices, selectedOrgId]);

  const selectedServiceObj = findServiceByName(availableServices, selectedService);

  /**
   * Adding either half of the Workflows <-> MyApp Professional pair creates the
   * other, server-side and in one transaction (item 6). The dialog must say so
   * BEFORE the user confirms — a second subscription appearing unannounced,
   * with its own price, is a billing surprise.
   *
   * False when the org already holds the other half: nothing extra is created.
   */
  const liveOrgSubsOf = (serviceName: string) =>
    orgData.filter(
      (sub) =>
        sub.service_name === serviceName &&
        ["active", "trialing", "trial"].includes(
          (sub.status ?? "").toLowerCase(),
        ),
    );
  /**
   * Adding Workflows to an org that holds MyApp on a tier OTHER than
   * Professional is refused by the API: a second MyApp subscription cannot be
   * added, and it will not auto-upgrade (Professional is R1,999/month). The
   * dialog must block it and name the fix, rather than promise "both will be
   * added" and then show the rejection — which is what Common Good Foundation
   * (myapp/BUSINESS) hit on 2026-09-28.
   *
   * Only when the org's rows are KNOWN (`anchorKnown`, same reasoning as the
   * bundled filter above): the FE must never be stricter than the BE.
   */
  const heldMyappTierBlockingWorkflows: string | null =
    anchorKnown && selectedService === PAIRED_WORKFLOWS
      ? (liveOrgSubsOf(PAIRED_MYAPP).find(
          (sub) => (sub.tier ?? "").toLowerCase() !== PAIRED_MYAPP_TIER,
        )?.tier ?? null)
      : null;
  const addCreatesPair =
    isPairedHalf(selectedService, selectedTier) &&
    heldMyappTierBlockingWorkflows === null &&
    !liveOrgSubsOf(otherHalfOf(selectedService)).some(
      (sub) =>
        otherHalfOf(selectedService) !== PAIRED_MYAPP ||
        (sub.tier ?? "").toLowerCase() === PAIRED_MYAPP_TIER,
    );
  const isConnectorService = selectedServiceObj?.category === "connector";
  const filteredB2cConnectors = b2cConnectors.filter((c) => {
    const b2cType = selectedServiceObj?.b2c_connector_type;
    if (!b2cType) return true;
    return c.SourceConnectorType === b2cType;
  });

  async function handleAddSubscription() {
    if (!token || !selectedOrgId || !selectedService || !selectedTier) return;

    setIsAdding(true);
    try {
      await platformApi.addServiceSubscription(token, selectedOrgId, {
        service_name: selectedService,
        tier: selectedTier,
        ...(isConnectorService && connectorInstanceId
          ? { client_connector_id: parseInt(connectorInstanceId, 10) }
          : {}),
      });
      toast.success(
        addCreatesPair
          ? "Workflows and MyApp Professional added together"
          : isConnectorService
            ? "Connector subscription added successfully"
            : "Subscription added successfully",
      );
      setShowAddDialog(false);
      setSelectedService("");
      setSelectedTier("");
      setConnectorInstanceId("");
      refresh();
    } catch (error) {
      console.error("Failed to add subscription:", error);
      const msg =
        error instanceof Error ? error.message : "Failed to add subscription";
      toast.error(msg);
    } finally {
      setIsAdding(false);
    }
  }

  function findServiceByName(
    services: PlatformService[],
    serviceName: string,
  ): PlatformService | undefined {
    return services.find(
      (s) =>
        s.name === serviceName ||
        s.display_name === serviceName ||
        s.name.toLowerCase() === serviceName.toLowerCase(),
    );
  }

  function openUpgradeDialog(subscription: Subscription) {
    setSubscriptionToUpgrade(subscription);
    setNewTier(subscription.tier);
    setUpgradeTiers([]);
    setShowUpgradeDialog(true);
    if (!token) return;

    const fetchTiers = (services: PlatformService[]) => {
      const service = findServiceByName(services, subscription.service_name);
      if (service) {
        platformApi
          .listTiersForService(token, service.id)
          .then((tiers) => setUpgradeTiers(tiers.filter((t) => t.is_active)))
          .catch(() => setUpgradeTiers([]));
      }
    };

    if (availableServices.length > 0) {
      fetchTiers(availableServices);
    } else {
      platformApi
        .listPlatformServices(token, false)
        .then((services) => {
          setAvailableServices(services);
          fetchTiers(services);
        })
        .catch(() => setUpgradeTiers([]));
    }
  }

  async function handleUpgradeSubscription() {
    if (!token || !subscriptionToUpgrade || !newTier) return;
    if (newTier === subscriptionToUpgrade.tier) {
      toast.error("Please select a different tier");
      return;
    }

    const isUpgradeChange = isUpgrade(subscriptionToUpgrade.tier, newTier);

    setIsUpgrading(true);
    try {
      const result = await platformApi.upgradeServiceTier(
        token,
        subscriptionToUpgrade.organisation_id,
        subscriptionToUpgrade.service_name,
        newTier,
        // The clicked row, not the newest one for the service (dp #2284).
        subscriptionToUpgrade.client_connector_id ?? null,
      );
      // PREFER THE SERVER'S MESSAGE (cp #574); the variant and fallback rules
      // live in `tierChangeToast`. Since cp #707 every upgrade defers its
      // prorated difference to the next monthly invoice by design
      // (`proration_on_next_invoice`), which is a SUCCESS — only the cp #574
      // "charge could not be taken" shape still warns. `richColors` on the
      // Toaster gives the warning variant distinct amber styling; duration is
      // already 10s globally (main.tsx).
      const outcome = tierChangeToast(result, newTier, isUpgradeChange);
      if (outcome.variant === "warning") {
        toast.warning(outcome.message);
      } else {
        toast.success(outcome.message);
      }
      setShowUpgradeDialog(false);
      setSubscriptionToUpgrade(null);
      setNewTier("");
      refresh();
    } catch (error) {
      console.error("Failed to change subscription tier:", error);
      // The CP's refusal detail (a retired tier, Reporting Bundled without an
      // anchor, a pairing break) is the useful part — quote it.
      const detail = error instanceof Error && error.message ? error.message : null;
      toast.error(detail ?? "Failed to change subscription tier");
    } finally {
      setIsUpgrading(false);
    }
  }

  function openCancelDialog(subscription: Subscription) {
    setSubscriptionToCancel(subscription);
    setCancelImmediately(false);
    setShowCancelDialog(true);
  }

  async function handleCancelSubscription() {
    if (!token || !subscriptionToCancel) return;

    setIsCancelling(true);
    try {
      await platformApi.cancelServiceSubscription(
        token,
        subscriptionToCancel.organisation_id,
        subscriptionToCancel.service_name,
        cancelImmediately,
        // Both halves, when the pair would otherwise be stranded. The user has
        // just confirmed a dialog that says both are going.
        cancelStrandsPair,
        // The clicked row, not the newest one for the service (dp #2284).
        subscriptionToCancel.client_connector_id ?? null,
      );
      toast.success(
        cancelStrandsPair
          ? "Workflows and MyApp Professional cancelled together"
          : cancelImmediately
            ? "Subscription cancelled immediately"
            : "Subscription will be cancelled at end of billing period",
      );
      setShowCancelDialog(false);
      setSubscriptionToCancel(null);
      refresh();
    } catch (error) {
      console.error("Failed to cancel subscription:", error);
      // Quote the CP's refusal (e.g. the 422 asking for the connector on a
      // per-connector service, dp #2284), as the tier-change handler does.
      const detail = error instanceof Error && error.message ? error.message : null;
      toast.error(detail ?? "Failed to cancel subscription");
    } finally {
      setIsCancelling(false);
    }
  }

  const getUpgradeTierOptions = (currentTier: string) => {
    const options = withoutBundled(
      upgradeTiers,
      subscriptionToUpgrade?.service_name,
    ).filter((t) => t.name.toUpperCase() !== currentTier.toUpperCase());
    // Item 6: moving MyApp off Professional while Workflows is held breaks the
    // pair, and the API 422s it. Offering the option and then rejecting the
    // save is worse than not offering it — this is the QUIET break, since
    // nothing is cancelled and the user would not connect the two.
    if (
      subscriptionToUpgrade?.service_name === PAIRED_MYAPP &&
      currentTier.toLowerCase() === PAIRED_MYAPP_TIER &&
      holdsOtherHalf(subscriptionToUpgrade)
    ) {
      return options.filter(
        (t) => t.name.toLowerCase() === PAIRED_MYAPP_TIER,
      );
    }
    return options;
  };

  const isUpgrade = (currentTier: string, newTierName: string) => {
    const currentOrder =
      upgradeTiers.find((t) => t.name.toUpperCase() === currentTier.toUpperCase())
        ?.sort_order ?? 0;
    const newOrder =
      upgradeTiers.find((t) => t.name.toUpperCase() === newTierName.toUpperCase())
        ?.sort_order ?? 0;
    return newOrder > currentOrder;
  };

  async function handleCancelScheduledDowngrade(subscription: Subscription) {
    if (!token) return;

    try {
      await platformApi.cancelScheduledTierChange(
        token,
        subscription.organisation_id,
        subscription.service_name,
        subscription.client_connector_id ?? null,
      );
      toast.success("Scheduled downgrade cancelled");
      refresh();
    } catch (error) {
      console.error("Failed to cancel scheduled downgrade:", error);
      // Quote the CP's refusal (e.g. the 422 asking for the connector on a
      // per-connector service, dp #2284), as the tier-change handler does.
      const detail = error instanceof Error && error.message ? error.message : null;
      toast.error(detail ?? "Failed to cancel scheduled downgrade");
    }
  }

  async function handleReactivateSubscription(subscription: Subscription) {
    if (!token) return;

    try {
      await platformApi.reactivateServiceSubscription(
        token,
        subscription.organisation_id,
        subscription.service_name,
        subscription.client_connector_id ?? null,
      );
      toast.success("Subscription reactivated — cancellation has been reversed");
      refresh();
    } catch (error) {
      console.error("Failed to reactivate subscription:", error);
      // Quote the CP's refusal (e.g. the 422 asking for the connector on a
      // per-connector service, dp #2284), as the tier-change handler does.
      const detail = error instanceof Error && error.message ? error.message : null;
      toast.error(detail ?? "Failed to reactivate subscription");
    }
  }

  function handleSort(column: string) {
    if (sortBy === column) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortBy(column);
      setSortOrder("asc");
    }
  }
  function getSortDirection(column: string): "asc" | "desc" | null {
    return sortBy === column ? sortOrder : null;
  }

  async function toggleFeatures(sub: Subscription) {
    const key = `${sub.organisation_id}::${sub.service_name}::features`;
    if (expandedFeatures.has(key)) {
      setExpandedFeatures((prev) => {
        const next = new Set(prev);
        next.delete(key);
        return next;
      });
      return;
    }

    setExpandedFeatures((prev) => new Set(prev).add(key));

    if (featuresCache[key]) return;

    const service = findServiceByName(availableServices, sub.service_name);
    if (!service || !token) return;

    setFeaturesLoading((prev) => new Set(prev).add(key));
    try {
      const features = await platformApi.getEffectiveFeatures(
        token,
        sub.organisation_id,
        service.id,
      );
      setFeaturesCache((prev) => ({ ...prev, [key]: features }));
    } catch (error) {
      console.error("Failed to load features:", error);
      // Don't toast — features are informational, degrade gracefully
      setFeaturesCache((prev) => ({ ...prev, [key]: [] }));
    } finally {
      setFeaturesLoading((prev) => {
        const next = new Set(prev);
        next.delete(key);
        return next;
      });
    }
  }

  function formatCurrency(cents: number, currency: string = "ZAR"): string {
    const amount = cents / 100;
    if (currency === "ZAR") return `R${amount.toLocaleString("en-ZA", { minimumFractionDigits: 2 })}`;
    return `$${amount.toLocaleString("en-US", { minimumFractionDigits: 2 })}`;
  }

  const stats = useMemo(() => {
    if (isOrgView) {
      return {
        total: filteredOrgData.length,
        active: filteredOrgData.filter((s) => s.status.toLowerCase() === "active").length,
      };
    }
    return {
      total: pagination.totalCount,
      active: adminData.filter((s) => s.status.toLowerCase() === "active").length,
    };
  }, [isOrgView, filteredOrgData, pagination.totalCount, adminData]);

  const getStatusBadge = (sub: Subscription) => {
    const status = sub.status.toLowerCase();

    // Active but marked for cancellation at period end
    if (status === "active" && sub.cancel_at_period_end) {
      return (
        <Badge variant="warning">
          <Clock className="mr-1 h-3 w-3" />
          Cancelling
        </Badge>
      );
    }

    switch (status) {
      case "active":
        return (
          <Badge variant="success">
            <CheckCircle className="mr-1 h-3 w-3" />
            Active
          </Badge>
        );
      case "cancelled":
        return (
          <Badge variant="destructive">
            <XCircle className="mr-1 h-3 w-3" />
            Cancelled
          </Badge>
        );
      case "pending":
        return (
          <Badge variant="secondary">
            <Clock className="mr-1 h-3 w-3" />
            Pending
          </Badge>
        );
      default:
        return <Badge variant="outline">{sub.status}</Badge>;
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  if (!selectedOrgId) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-4xl font-bold text-slate-900">Subscriptions</h1>
          <p className="mt-2 text-lg text-slate-600">
            Manage service subscriptions
          </p>
        </div>
        <div className="rounded-3xl border border-slate-100 bg-white p-6 shadow-sm">
          <div className="text-center py-12 text-muted-foreground">
            <CreditCard className="h-12 w-12 mx-auto mb-4 opacity-50" />
            <p>Select an organisation from the header to view subscriptions</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-bold text-slate-900">Subscriptions</h1>
          <p className="mt-2 text-lg text-slate-600">
            {isOrgView
              ? `Manage service subscriptions for ${currentOrgName}`
              : "All service subscriptions across organisations"}
          </p>
        </div>
        {/* Admin view toggle */}
        {isPlatformAdmin && (
          <div className="flex items-center rounded-lg border border-slate-200 bg-slate-50 p-1">
            <Button
              variant={isOrgView ? "default" : "ghost"}
              size="sm"
              onClick={() => handleViewModeChange("organisation")}
              className="gap-1.5"
            >
              <Building2 className="h-3.5 w-3.5" />
              {currentOrgName}
            </Button>
            <Button
              variant={!isOrgView ? "default" : "ghost"}
              size="sm"
              onClick={() => handleViewModeChange("all")}
              className="gap-1.5"
            >
              All Organisations
            </Button>
          </div>
        )}
      </div>

      {/* Content Panel */}
      <div className="rounded-3xl border border-slate-100 bg-white p-6 shadow-sm">
        {/* Controls bar */}
        <div className="flex flex-wrap items-center gap-3 mb-6">
          <SearchInput
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onClear={() => setSearchTerm("")}
            placeholder={isOrgView ? "Search by service name..." : "Search by organisation or service..."}
            containerClassName="flex-1 min-w-[200px]"
          />
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-[150px]">
              <SelectValue placeholder="All Statuses" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Statuses</SelectItem>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="cancelled">Cancelled</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
            </SelectContent>
          </Select>
          <Select value={categoryFilter} onValueChange={setCategoryFilter}>
            <SelectTrigger className="w-[160px]">
              <SelectValue placeholder="All Categories" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Categories</SelectItem>
              <SelectItem value="platform">Platform</SelectItem>
              <SelectItem value="connector">Connector</SelectItem>
              <SelectItem value="managed_service">Managed services</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" size="sm" onClick={refresh}>
            {isLoading ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <RefreshCw className="h-4 w-4 mr-2" />
            )}
            Refresh
          </Button>
          {canManageSubscriptions && isOrgView && (
            <Button size="sm" onClick={() => setShowAddDialog(true)}>
              <Plus className="mr-2 h-4 w-4" />
              Add Subscription
            </Button>
          )}
        </div>

        {/* Stats Cards */}
        <div className="grid gap-4 md:grid-cols-2 mb-6">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">
                Total Subscriptions
              </CardTitle>
              <CreditCard className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.total}</div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">
                Active Subscriptions
              </CardTitle>
              <CheckCircle className="h-4 w-4 text-green-500" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.active}</div>
            </CardContent>
          </Card>
        </div>

        {/* Table */}
        {isLoading && data.length === 0 ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        ) : data.length === 0 ? (
          <div className="text-center py-12">
            <CreditCard className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <h3 className="text-lg font-medium mb-2">
              {searchTerm ? "No subscriptions match your search" : "No subscriptions yet"}
            </h3>
            <p className="text-muted-foreground mb-4">
              {searchTerm ? "Try a different search term." : "Add a subscription to get started."}
            </p>
            {!searchTerm && canManageSubscriptions && isOrgView && (
              <Button onClick={() => setShowAddDialog(true)}>
                <Plus className="h-4 w-4 mr-2" />
                Add Subscription
              </Button>
            )}
          </div>
        ) : (
          <>
            <Table>
              <TableHeader>
                <TableRow>
                  {!isOrgView && (
                    <SortableTableHead
                      direction={getSortDirection("organisation_name")}
                      onSort={() => handleSort("organisation_name")}
                    >
                      Organisation
                    </SortableTableHead>
                  )}
                  <SortableTableHead
                    direction={getSortDirection("service_name")}
                    onSort={() => handleSort("service_name")}
                  >
                    Service
                  </SortableTableHead>
                  <SortableTableHead
                    direction={getSortDirection("category")}
                    onSort={() => handleSort("category")}
                  >
                    Category
                  </SortableTableHead>
                  <SortableTableHead
                    direction={getSortDirection("tier")}
                    onSort={() => handleSort("tier")}
                  >
                    Tier
                  </SortableTableHead>
                  <SortableTableHead
                    direction={getSortDirection("status")}
                    onSort={() => handleSort("status")}
                  >
                    Status
                  </SortableTableHead>
                  <SortableTableHead
                    direction={getSortDirection("created_at")}
                    onSort={() => handleSort("created_at")}
                  >
                    Created
                  </SortableTableHead>
                  {canManageSubscriptions && (
                    <SortableTableHead direction={null} onSort={() => {}} disabled className="w-[50px]">
                      {""}
                    </SortableTableHead>
                  )}
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.map((sub, index) => {
                  const subKey = `${sub.organisation_id}::${sub.service_name}`;
                  const colCount = (isOrgView ? 5 : 6) + (canManageSubscriptions ? 1 : 0);
                  const featKey = `${subKey}::features`;
                  const isFeatExpanded = expandedFeatures.has(featKey);
                  const featData = featuresCache[featKey];
                  const isFeatLoading = featuresLoading.has(featKey);

                  return (
                    <React.Fragment key={`${sub.organisation_id}-${sub.service_name}-${index}`}>
                      <TableRow>
                        {!isOrgView && (
                          <TableCell className="font-medium">
                            {sub.organisation_name}
                          </TableCell>
                        )}
                        <TableCell className="font-medium">
                          <div className="flex items-center gap-2">
                            {sub.service_display_name || sub.service_name}
                            {sub.client_connector_id && (
                              <Badge variant="outline" className="text-xs">
                                #{sub.client_connector_id}
                              </Badge>
                            )}
                            {sub.category !== "platform" && (
                              <Badge variant="secondary" className="text-xs">
                                {categoryLabel(sub.category)}
                              </Badge>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant={sub.category === "connector" ? "outline" : "secondary"}>
                            {categoryLabel(sub.category)}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div className="space-y-1">
                            <div className="flex items-center gap-2">
                              <Badge variant="outline">{sub.tier}</Badge>
                              {sub.status.toLowerCase() === "active" && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-6 px-2 text-xs text-muted-foreground hover:text-foreground"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    toggleFeatures(sub);
                                  }}
                                >
                                  {isFeatLoading ? (
                                    <Loader2 className="h-3 w-3 animate-spin" />
                                  ) : isFeatExpanded ? (
                                    <ChevronDown className="h-3 w-3 mr-1" />
                                  ) : (
                                    <ChevronRight className="h-3 w-3 mr-1" />
                                  )}
                                  Features
                                </Button>
                              )}
                            </div>
                            {sub.scheduled_tier && (
                              <div className="flex items-center gap-1 text-xs text-amber-600">
                                <Clock className="h-3 w-3" />
                                <span>
                                  → {sub.scheduled_tier} on{" "}
                                  {sub.scheduled_tier_effective_date
                                    ? formatDate(sub.scheduled_tier_effective_date)
                                    : "period end"}
                                </span>
                                {canManageSubscriptions && (
                                  <Button
                                    variant="link"
                                    size="sm"
                                    className="h-auto p-0 text-xs text-amber-600 hover:text-amber-800"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleCancelScheduledDowngrade(sub);
                                    }}
                                  >
                                    Cancel
                                  </Button>
                                )}
                              </div>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>{getStatusBadge(sub)}</TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {formatDate(sub.created_at)}
                        </TableCell>
                        {canManageSubscriptions && (
                          <TableCell onClick={(e) => e.stopPropagation()}>
                            {sub.status.toLowerCase() === "active" && (
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <Button variant="ghost" size="icon">
                                    <MoreHorizontal className="h-4 w-4" />
                                  </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end">
                                  {sub.cancel_at_period_end ? (
                                    <DropdownMenuItem onClick={() => handleReactivateSubscription(sub)}>
                                      <RotateCcw className="mr-2 h-4 w-4" />
                                      Reactivate
                                    </DropdownMenuItem>
                                  ) : (
                                    <>
                                      <DropdownMenuItem onClick={() => openUpgradeDialog(sub)}>
                                        <ArrowUp className="mr-2 h-4 w-4" />
                                        Change Tier
                                      </DropdownMenuItem>
                                      {isPlatformAdmin && sub.id && (
                                        <DropdownMenuItem onClick={() => setSubscriptionForBillingStart(sub)}>
                                          <CalendarClock className="mr-2 h-4 w-4" />
                                          Set billing start
                                        </DropdownMenuItem>
                                      )}
                                      <DropdownMenuSeparator />
                                      <DropdownMenuItem
                                        onClick={() => openCancelDialog(sub)}
                                        className="text-destructive focus:text-destructive"
                                      >
                                        <Ban className="mr-2 h-4 w-4" />
                                        Cancel
                                      </DropdownMenuItem>
                                    </>
                                  )}
                                </DropdownMenuContent>
                              </DropdownMenu>
                            )}
                          </TableCell>
                        )}
                      </TableRow>

                      {/* Expanded features sub-row */}
                      {isFeatExpanded && (
                        <TableRow className="bg-slate-50/50 hover:bg-slate-50/50">
                          <TableCell colSpan={colCount} className="p-0">
                            <div className="px-8 py-4 border-t border-slate-100">
                              <div className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-2">
                                Features
                              </div>
                              {isFeatLoading ? (
                                <div className="flex items-center gap-2 py-2 text-sm text-muted-foreground">
                                  <Loader2 className="h-4 w-4 animate-spin" />
                                  Loading features...
                                </div>
                              ) : !featData || featData.length === 0 ? (
                                <div className="text-sm text-muted-foreground py-2">
                                  No features configured for this service yet.
                                </div>
                              ) : (
                                <div className="grid gap-2">
                                  {featData.map((feat) => {
                                    const source = String(feat.source || "");
                                    const isIncluded = source === "tier_included" || feat.is_base;
                                    const priceZar = Number(feat.price_monthly_zar || 0);
                                    const priceUsd = Number(feat.price_monthly_usd || 0);
                                    return (
                                      <div
                                        key={String(feat.feature_key)}
                                        className="flex items-center justify-between rounded border px-3 py-2"
                                      >
                                        <div className="flex items-center gap-2">
                                          <CheckCircle className={`h-4 w-4 ${isIncluded ? "text-green-500" : "text-blue-500"}`} />
                                          <span className="text-sm">{String(feat.display_name)}</span>
                                          {feat.sku_code ? (
                                            <span className="text-xs text-muted-foreground">({String(feat.sku_code)})</span>
                                          ) : null}
                                        </div>
                                        <div className="flex items-center gap-2">
                                          {isIncluded ? (
                                            <Badge variant="secondary" className="text-xs">Included</Badge>
                                          ) : (
                                            <Badge variant="outline" className="text-xs">
                                              Add-on
                                              {priceZar > 0 && ` · ${formatCurrency(priceZar, "ZAR")}/mo`}
                                              {priceUsd > 0 && priceZar === 0 && ` · ${formatCurrency(priceUsd, "USD")}/mo`}
                                            </Badge>
                                          )}
                                        </div>
                                      </div>
                                    );
                                  })}
                                </div>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      )}
                    </React.Fragment>
                  );
                })}
              </TableBody>
            </Table>

            {/* Pagination — only for admin view (org view is not paginated) */}
            {!isOrgView && pagination.totalCount > 0 && (
              <div className="mt-4 border-t pt-4">
                <Pagination
                  page={pagination.page}
                  totalPages={pagination.totalPages}
                  totalCount={pagination.totalCount}
                  pageSize={pagination.pageSize}
                  pageSizeOptions={[10, 25, 50, 100]}
                  startIndex={startIndex}
                  endIndex={endIndex}
                  canGoPrev={canGoPrev}
                  canGoNext={canGoNext}
                  onPageChange={goToPage}
                  onPageSizeChange={setPageSize}
                  onFirstPage={firstPage}
                  onLastPage={lastPage}
                  onPrevPage={prevPage}
                  onNextPage={nextPage}
                />
              </div>
            )}
          </>
        )}
      </div>

      {/* Add Subscription Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Service Subscription</DialogTitle>
            <DialogDescription>
              Subscribe {currentOrgName} to a new service
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {addCreatesPair && (
              <div
                data-testid="add-pair-notice"
                className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900"
              >
                Workflows and MyApp Professional may only be held together, so{" "}
                <strong>both will be added</strong> and both will be billed.
              </div>
            )}
            {heldMyappTierBlockingWorkflows !== null && (
              <div
                data-testid="add-pair-blocked"
                className="rounded-lg border border-red-300 bg-red-50 p-3 text-sm text-red-900"
              >
                Workflows may only be held together with MyApp Professional, and{" "}
                {currentOrgName} holds MyApp on{" "}
                <strong>{heldMyappTierBlockingWorkflows.toLowerCase()}</strong>.
                Upgrade MyApp to Professional first, then add Workflows.
              </div>
            )}
            <div className="space-y-2">
              <Label>Service</Label>
              <Select value={selectedService} onValueChange={setSelectedService}>
                <SelectTrigger>
                  <SelectValue placeholder="Select a service..." />
                </SelectTrigger>
                <SelectContent>
                  {availableServices
                    .filter((s) => s.is_active && s.is_launched)
                    .filter(
                      (s) =>
                        // Connector services can have multiple subscriptions
                        s.category === "connector" ||
                        !orgData.some(
                          (sub) =>
                            sub.service_name === s.name &&
                            sub.status === "active",
                        ),
                    )
                    .sort((a, b) => (a.display_name || a.name).localeCompare(b.display_name || b.name))
                    .map((service) => (
                      <SelectItem key={service.id} value={service.name}>
                        {service.display_name}
                        {service.category === "connector" && (
                          <span className="text-xs text-muted-foreground ml-2">(connector)</span>
                        )}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Tier</Label>
              <Select
                value={selectedTier}
                onValueChange={setSelectedTier}
                disabled={!selectedService || isLoadingTiers}
              >
                <SelectTrigger>
                  <SelectValue
                    placeholder={
                      isLoadingTiers
                        ? "Loading tiers..."
                        : !selectedService
                          ? "Select a service first..."
                          : "Select a tier..."
                    }
                  />
                </SelectTrigger>
                <SelectContent>
                  {withoutBundled(serviceTiers, selectedService).map((tier) => (
                    <SelectItem key={tier.id} value={tier.name.toUpperCase()}>
                      {tier.display_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {hideReportingBundled(selectedService) && (
                <p data-testid="bundled-anchor-hint" className="text-xs text-muted-foreground">
                  {BUNDLED_ANCHOR_HINT}
                </p>
              )}
            </div>
            {isConnectorService && (
              <div className="space-y-2">
                <Label>Connector Instance</Label>
                {isLoadingB2cConnectors ? (
                  <div className="flex items-center gap-2 text-sm text-muted-foreground py-2">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Loading connector instances...
                  </div>
                ) : b2cFetchFailed ? (
                  <p className="text-sm text-destructive py-2">
                    Failed to load connector instances. B2C service is unavailable.
                  </p>
                ) : filteredB2cConnectors.length === 0 ? (
                  <p className="text-sm text-muted-foreground py-2">
                    No {selectedServiceObj?.display_name ?? "connector"} instances found for this organisation
                  </p>
                ) : (
                  <Select value={connectorInstanceId} onValueChange={setConnectorInstanceId}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select a connector instance..." />
                    </SelectTrigger>
                    <SelectContent>
                      {[...filteredB2cConnectors]
                        .sort((a, b) => (a.Name || "").localeCompare(b.Name || ""))
                        .map((c) => (
                          <SelectItem key={c.Id} value={String(c.Id)}>
                            {c.Name || `Instance #${c.Id}`}
                            <span className="text-xs text-muted-foreground ml-2">
                              #{c.Id}
                            </span>
                          </SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                )}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowAddDialog(false)}
              disabled={isAdding}
            >
              Cancel
            </Button>
            <Button
              onClick={handleAddSubscription}
              disabled={
                isAdding ||
                !selectedService ||
                !selectedTier ||
                heldMyappTierBlockingWorkflows !== null ||
                (isConnectorService && !connectorInstanceId)
              }
            >
              {isAdding && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {isConnectorService ? "Add Connector Subscription" : "Add Subscription"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Upgrade Subscription Dialog */}
      <Dialog open={showUpgradeDialog} onOpenChange={setShowUpgradeDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Change Subscription Tier</DialogTitle>
            <DialogDescription>
              Update the tier for {subscriptionToUpgrade?.service_display_name || subscriptionToUpgrade?.service_name}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Current Tier</Label>
              <div className="p-2 bg-muted rounded-md">
                <Badge variant="outline">{subscriptionToUpgrade?.tier}</Badge>
              </div>
            </div>
            <div className="space-y-2">
              <Label>New Tier</Label>
              <Select value={newTier} onValueChange={setNewTier}>
                <SelectTrigger>
                  <SelectValue placeholder="Select new tier..." />
                </SelectTrigger>
                <SelectContent>
                  {subscriptionToUpgrade &&
                    getUpgradeTierOptions(subscriptionToUpgrade.tier).map((tier) => (
                      <SelectItem key={tier.id} value={tier.name.toUpperCase()}>
                        {tier.display_name}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
              {hideReportingBundled(subscriptionToUpgrade?.service_name) && (
                <p data-testid="bundled-anchor-hint" className="text-xs text-muted-foreground">
                  {BUNDLED_ANCHOR_HINT}
                </p>
              )}
            </div>
            {/* Show different messaging for upgrade vs downgrade */}
            {newTier && subscriptionToUpgrade && newTier !== subscriptionToUpgrade.tier && (
              <div className={`p-3 rounded-md text-sm ${
                isUpgrade(subscriptionToUpgrade.tier, newTier)
                  ? "bg-green-50 text-green-800 border border-green-200"
                  : "bg-amber-50 text-amber-800 border border-amber-200"
              }`}>
                {isUpgrade(subscriptionToUpgrade.tier, newTier) ? (
                  <>
                    <strong>Upgrade:</strong> Your new tier will be active immediately.
                    Nothing is charged now — the prorated difference will be added to
                    your next monthly invoice.
                  </>
                ) : (
                  <>
                    <strong>Downgrade:</strong> Your current {subscriptionToUpgrade.tier} features
                    will remain active until the end of your billing period
                    {subscriptionToUpgrade.current_period_end && (
                      <> ({formatDate(subscriptionToUpgrade.current_period_end)})</>
                    )}.
                    The new tier will take effect on your next billing date.
                  </>
                )}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowUpgradeDialog(false)}
              disabled={isUpgrading}
            >
              Cancel
            </Button>
            <Button
              onClick={handleUpgradeSubscription}
              disabled={
                isUpgrading ||
                !newTier ||
                newTier === subscriptionToUpgrade?.tier
              }
            >
              {isUpgrading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {newTier && subscriptionToUpgrade && isUpgrade(subscriptionToUpgrade.tier, newTier)
                ? "Upgrade Now"
                : "Schedule Downgrade"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Cancel Subscription Dialog */}
      <Dialog open={showCancelDialog} onOpenChange={setShowCancelDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Cancel Subscription</DialogTitle>
            <DialogDescription>
              Are you sure you want to cancel the {subscriptionToCancel?.service_display_name || subscriptionToCancel?.service_name}{" "}
              subscription{!isOrgView && subscriptionToCancel ? ` for ${subscriptionToCancel.organisation_name}` : ""}?
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {cancelStrandsPair && (
              <div
                data-testid="cancel-pair-warning"
                className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900"
              >
                Workflows and MyApp Professional are held together, so{" "}
                <strong>both will be cancelled</strong>. Cancelling only one
                would leave the other unusable.
              </div>
            )}
            {isPlatformAdmin ? (
              <>
                <div className="space-y-3">
                  <Label>Cancellation type</Label>
                  <div className="space-y-2">
                    <label className="flex items-start gap-3 rounded-lg border p-3 cursor-pointer hover:bg-slate-50">
                      <input
                        type="radio"
                        name="cancelType"
                        checked={!cancelImmediately}
                        onChange={() => setCancelImmediately(false)}
                        className="mt-0.5"
                      />
                      <div>
                        <div className="font-medium text-sm">At end of billing period</div>
                        <div className="text-xs text-muted-foreground">
                          Access continues until{" "}
                          {subscriptionToCancel?.current_period_end
                            ? formatDate(subscriptionToCancel.current_period_end)
                            : "the period ends"}
                        </div>
                      </div>
                    </label>
                    <label className="flex items-start gap-3 rounded-lg border border-red-200 p-3 cursor-pointer hover:bg-red-50">
                      <input
                        type="radio"
                        name="cancelType"
                        checked={cancelImmediately}
                        onChange={() => setCancelImmediately(true)}
                        className="mt-0.5"
                      />
                      <div>
                        <div className="font-medium text-sm text-red-700">Cancel immediately</div>
                        <div className="text-xs text-muted-foreground">
                          Access is revoked right now. Use for cleanup or incorrect subscriptions.
                        </div>
                      </div>
                    </label>
                  </div>
                </div>
              </>
            ) : (
              <p className="text-sm text-muted-foreground">
                The subscription will be cancelled at the end of the current billing
                period. You will retain access until then.
              </p>
            )}
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowCancelDialog(false)}
              disabled={isCancelling}
            >
              Keep Subscription
            </Button>
            <Button
              variant="destructive"
              onClick={handleCancelSubscription}
              disabled={isCancelling}
            >
              {isCancelling && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {cancelImmediately ? "Cancel Immediately" : "Cancel at Period End"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Billing Start Dialog (platform admin only, cp #843) */}
      {isPlatformAdmin && (
        <SubscriptionBillingStartDialog
          subscription={subscriptionForBillingStart}
          token={token}
          showOrganisationName={!isOrgView}
          onClose={() => setSubscriptionForBillingStart(null)}
          onSaved={refresh}
        />
      )}

    </div>
  );
}
